import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, type ReactNode, useCallback, useRef, useState } from "react";
import type { PopoverAlign, PopoverPosition } from "react-tiny-popover";
import { ALPHA, CONTENT_WIDTH, ICON_SIZE, MOTION } from "../../../theme/tokens";
import { SYSTEM_POPOVER_VIEWPORT_INSET, SystemReactTinyPopover, SystemReactTinyPopoverRootStyle } from "../Popups/SystemReactTinyPopover";
import SystemButton from "../SystemButton/SystemButton";
import type { InlineHintTone } from "./InlineHint";
import { type OneTimeNoticeCampaign, OneTimeNoticeContent, useOneTimeNotice } from "./OneTimeNotice";

/** Where the popover prefers to open, and how it lines up with the trigger. */
const DEFAULT_POSITIONS: PopoverPosition[] = ["bottom", "top", "left"];
const DEFAULT_ALIGN: PopoverAlign = "end";

/** The glyph settles in on mount so a marker that was not there yesterday is noticed, not just seen. */
const TRIGGER_ENTRANCE_SX: SxProps<Theme> = {
	display: "inline-flex",
	"@keyframes oneTimeNoticeTriggerEnter": {
		from: { opacity: 0, transform: "scale(0.9)" },
		to: { opacity: 1, transform: "scale(1)" },
	},
	animation: `oneTimeNoticeTriggerEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

type Props = OneTimeNoticeCampaign & {
	/**
	 * Stable id for this notice. The acknowledgement is persisted per user under it, so changing it
	 * re-shows the notice to everyone — treat it as part of the campaign, not a cosmetic name.
	 */
	noticeKey: string;
	/** The message, already translated. */
	children: ReactNode;
	/** Label of the acknowledge button, already translated (e.g. "Understood", "Got it"). */
	dismissLabel: string;
	/** Accessible name **and** tooltip of the trigger, already translated (e.g. "What's new"). */
	triggerLabel: string;
	/**
	 * Semantic weight of the announcement. Required, not defaulted: it colors the trigger sitting in
	 * someone's toolbar, and a marker's meaning is the caller's to state rather than ours to guess.
	 * Resolves through `theme.palette[tone]`, so trigger and note are the same hue by construction.
	 */
	tone: Exclude<InlineHintTone, "neutral">;
	/** Override the trigger glyph. Defaults to the same info mark `InlineHint` leads with. */
	triggerIcon?: ReactNode;
	/** Override the glyph inside the note (e.g. a campaign horn for a rollout announcement). */
	noticeIcon?: ReactNode;
	positions?: PopoverPosition[];
	align?: PopoverAlign;
	dataTestId?: string;
};

/**
 * A `OneTimeNotice` parked **behind an icon** instead of in the layout: a tinted marker beside the
 * controls it is about, which opens the note and then disappears — for good — once acknowledged.
 *
 * Use it over a bare `OneTimeNotice` wherever the surface has no room to carry a paragraph that most
 * people will read once: a header's control cluster, a dense toolbar, a table's title row.
 *
 * The trigger and the note share **one** `useOneTimeNotice`, which is the point of the component:
 * gate them separately and an expired-or-already-acknowledged user still gets a marker that opens
 * onto nothing — strictly worse than no marker at all. Nothing renders once the campaign is over, so
 * the affordance leaves no gap behind it.
 */
export const OneTimeNoticePopover: FC<Props> = ({
	noticeKey,
	expiresAt,
	onlyForAccountsCreatedBefore,
	onDismiss,
	children,
	dismissLabel,
	triggerLabel,
	tone,
	triggerIcon,
	noticeIcon,
	positions = DEFAULT_POSITIONS,
	align = DEFAULT_ALIGN,
	dataTestId,
}) => {
	const { isEligible, dismiss } = useOneTimeNotice(noticeKey, { expiresAt, onlyForAccountsCreatedBefore, onDismiss });
	const [isOpen, setIsOpen] = useState(false);
	// Acknowledging makes the trigger ineligible immediately, but unmounting on that same tick would
	// cut the popover's close animation off mid-flight. Stay mounted until the exit has played out.
	const [isClosingAfterDismiss, setIsClosingAfterDismiss] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const handleAcknowledge = useCallback(() => {
		setIsClosingAfterDismiss(true);
		setIsOpen(false);
		dismiss();
	}, [dismiss]);

	// The popover is portaled to the end of <body>, so without this its "Understood" button sits at the
	// end of the tab order rather than after the trigger that opened it: focus moves into the note on
	// open, and comes back to the trigger on close.
	//
	// Both halves are deferred a frame, for different reasons. react-tiny-popover appends its portal
	// container in a layout effect, so at ref-attach time this node can still be DETACHED and `focus()`
	// a silent no-op — measured: not one `focusin` fired. And `document.body` holding focus on the way
	// out is the signal that the element that had it was just removed, i.e. it was ours to give back;
	// focus that the user has since put somewhere real is left exactly where they put it.
	const handleContentRef = useCallback((node: HTMLDivElement | null) => {
		if (node) {
			requestAnimationFrame(() => {
				if (node.isConnected) {
					node.focus();
				}
			});
			return;
		}
		if (document.activeElement === document.body) {
			// No-op after an acknowledgement, where the trigger is on its way out with the note.
			requestAnimationFrame(() => triggerRef.current?.focus());
		}
	}, []);

	if (!isEligible && !isClosingAfterDismiss) {
		return null;
	}

	return (
		<SystemReactTinyPopover
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			onClickOutside={() => setIsOpen(false)}
			onExitComplete={() => setIsClosingAfterDismiss(false)}
			closeOnEscape
			positions={positions}
			align={align}
			// No inner padding: the tinted note IS the surface here, corner to corner, rather than a
			// colored card floating inside a white one — which is also what ties the popover to the
			// trigger that opened it. Border-box + a viewport cap keep it on screen on a phone, where the
			// trigger sits a few pixels from the edge and the note is most of the screen wide.
			contentRootStyle={{
				...SystemReactTinyPopoverRootStyle,
				padding: 0,
				boxSizing: "border-box",
				maxWidth: `calc(100vw - ${SYSTEM_POPOVER_VIEWPORT_INSET * 2}px)`,
			}}
			data-testid={dataTestId ? `${dataTestId}-popover` : undefined}
			content={
				<Box
					ref={handleContentRef}
					// A non-modal dialog: focus moves in, Escape and an outside click close it, and the page
					// behind stays operable — so it is announced as what `aria-haspopup="dialog"` promised
					// without pretending to trap focus.
					role="dialog"
					aria-label={triggerLabel}
					tabIndex={-1}
					// `OneTimeNoticeContent` deliberately contributes no intrinsic width (so it can't stretch
					// the surfaces it is dropped into), which leaves the column for it to be measured here.
					sx={{ width: CONTENT_WIDTH.FLOATING_NOTICE, maxWidth: "100%", outline: "none" }}
				>
					<OneTimeNoticeContent
						// Always visible: inside a popover the popover's own open state IS the dismissal
						// animation, and folding the note away behind a closing surface just squashes it.
						isVisible
						onAcknowledge={handleAcknowledge}
						dismissLabel={dismissLabel}
						tone={tone}
						icon={noticeIcon}
						actionPlacement="below"
						dataTestId={dataTestId}
						// Matching the popover's own radius (read from it, never re-typed) so the two edges are
						// one edge, plus a touch more room than an inline hint gets — this note is the whole
						// surface, not a strip inside a form.
						sx={{ borderRadius: `${SystemReactTinyPopoverRootStyle.borderRadius}px`, px: 1.75, py: 1.5 }}
					>
						{children}
					</OneTimeNoticeContent>
				</Box>
			}
		>
			<SystemButton
				ref={triggerRef}
				buttonVariant="borderless"
				iconButton
				onClick={() => setIsOpen((open) => !open)}
				tooltip={triggerLabel}
				aria-label={triggerLabel}
				aria-haspopup="dialog"
				aria-expanded={isOpen}
				data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
				sx={(theme: Theme) => ({
					// A tinted disc rather than a loose colored glyph: beside a row of neutral controls it has
					// to read as a deliberate marker, and it is the one thing on this row asking to be clicked.
					color: theme.palette[tone].main,
					backgroundColor: alpha(theme.palette[tone].main, ALPHA.HINT_TINT),
					borderRadius: "50%",
					"&:hover": { backgroundColor: alpha(theme.palette[tone].main, ALPHA.CONTROL_TINT_HOVER) },
				})}
			>
				<Box component="span" sx={TRIGGER_ENTRANCE_SX}>
					{triggerIcon ?? <InfoOutlined sx={{ fontSize: ICON_SIZE.CONTROL }} />}
				</Box>
			</SystemButton>
		</SystemReactTinyPopover>
	);
};
